<div class="p2p-container" [class.sidebar-open]="sidebarOpen">
    <div class="p2p-main">
        <div class="page-header">
            <div class="label">PROCURE-TO-PAY WORKFLOW</div>
            <h2>Process Diagrams</h2>
            <p>Purchase Orders, Receiving, and Invoicing — connected end-to-end with logical handoff points between each phase.</p>
        </div>

        <div class="instructions">
            <strong>How to use:</strong> Click any node in the diagrams below to select it.
            The comments panel will open on the right where you can add notes, questions, or feedback
            for that specific process step. All comments are saved and attributed to your user account.
        </div>

        <!-- Phase 1: Purchase Orders -->
        <div class="process-section">
            <div class="process-header">
                <div class="process-number po">1</div>
                <div>
                    <h3>Purchase Order Creation</h3>
                    <p>Mobile-first PO creation with role-based inputs, conditional approval routing, and ERP integration</p>
                </div>
            </div>
            <div class="diagram-wrapper">
                <div class="mermaid" id="diagram-po" #diagramPo></div>
            </div>
        </div>

        <!-- Connector: PO → Receiving -->
        <div class="connector">
            <div class="con-line pr"></div>
            <div class="con-label pr">PO issued and active in Vista — awaiting delivery at job site</div>
            <div class="con-line pr"></div>
        </div>

        <!-- Phase 2: Receiving -->
        <div class="process-section">
            <div class="process-header">
                <div class="process-number recv">2</div>
                <div>
                    <h3>Receiving &amp; Delivery Tracking</h3>
                    <p>Partial delivery tracking, AI-assisted packing slip reading, and receiving record verification</p>
                </div>
            </div>
            <div class="diagram-wrapper">
                <div class="mermaid" id="diagram-recv" #diagramRecv></div>
            </div>
        </div>

        <!-- Connector: Receiving → Invoicing -->
        <div class="connector">
            <div class="con-line ri"></div>
            <div class="con-label ri">Receiving record in Vista — receipt data available for 3-way match during invoicing</div>
            <div class="con-line ri"></div>
        </div>

        <!-- Phase 3: Invoicing -->
        <div class="process-section">
            <div class="process-header">
                <div class="process-number inv">3</div>
                <div>
                    <h3>Invoice Processing</h3>
                    <p>Automated email ingestion, AI-powered extraction with confidence scoring, human review, and ERP upload</p>
                </div>
            </div>
            <div class="diagram-wrapper">
                <div class="mermaid" id="diagram-inv" #diagramInv></div>
            </div>
        </div>

        <!-- Combined Overview -->
        <div class="overview-section">
            <div class="process-header">
                <div>
                    <h3>End-to-End Connected Flow</h3>
                    <p>All three processes shown together with the handoff points that link PO creation through receiving to invoice payment</p>
                </div>
            </div>
            <div class="diagram-wrapper">
                <div class="mermaid" id="diagram-combined" #diagramCombined></div>
            </div>
        </div>

        <!-- All Comments Summary -->
        @if (comments.length > 0) {
            <div class="ac-section">
                <h3>All Comments</h3>
                <div class="ac-list">
                    @for (comment of comments; track comment.id) {
                        <div class="ac-row">
                            <div class="ac-node-info">
                                <div class="ac-node">{{ comment.nodeLabel }}</div>
                                <div class="ac-process">{{ comment.processName }}</div>
                            </div>
                            <div class="ac-text">{{ comment.comment }}</div>
                            <div class="ac-meta">
                                <div class="ac-author">{{ comment.createdByName || 'Unknown' }}</div>
                                <div class="ac-time">{{ comment.createdOn | date:'short' }}</div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        }
    </div>

    <!-- Sidebar Toggle Button -->
    <button class="sidebar-toggle" (click)="toggleSidebar()">
        Comments
        <span class="count-badge">{{ totalCommentCount }}</span>
    </button>

    <!-- Sidebar -->
    <div class="sidebar" [class.open]="sidebarOpen">
        <div class="sidebar-header">
            <h4>Annotations</h4>
            <button class="sidebar-close" (click)="closeSidebar()">&times;</button>
        </div>
        <div class="sidebar-body">
            @if (!selectedNodeId) {
                <div class="no-selection">
                    <div class="icon">click a node</div>
                    <p>Click any step in the diagrams to select it, then add your comments and notes here.</p>
                </div>
            } @else {
                <div class="sn-info">
                    <div class="sn-label">Selected Step</div>
                    <div class="sn-name">{{ selectedNodeLabel }}</div>
                    <div class="sn-process">{{ selectedNodeProcess }}</div>
                </div>
                <div class="comment-form">
                    <textarea
                        [(ngModel)]="newCommentText"
                        placeholder="Add a comment, question, or note about this step..."
                        (keydown)="onCommentKeydown($event)"
                    ></textarea>
                    <div class="comment-actions">
                        <button class="btn btn-primary btn-sm" (click)="addComment()">Add Comment</button>
                    </div>
                </div>
                <div class="comments-list">
                    @if (selectedNodeComments.length === 0) {
                        <div class="no-comments">No comments yet for this step.</div>
                    }
                    @for (comment of selectedNodeComments; track comment.id) {
                        <div class="comment-card">
                            <div class="comment-meta">
                                <span class="comment-author">{{ comment.createdByName || 'Unknown' }}</span>
                                <span class="comment-time">{{ comment.createdOn | date:'short' }}</span>
                                <button class="comment-delete" (click)="deleteComment(comment)" title="Delete">&times;</button>
                            </div>
                            <div class="comment-text">{{ comment.comment }}</div>
                        </div>
                    }
                </div>
            }
        </div>
    </div>
</div>
